/**
 * Form Validation Utilities
 * 
 * Provides validation rules, real-time validation, and error messages
 * for consistent form handling throughout the application.
 */

import { useState, useCallback, useRef } from 'react';

// Common validation patterns
export const patterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^[\d\s\-+()]+$/,
  url: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
  zipCode: /^\d{5}(-\d{4})?$/,
  slug: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  noSpecialChars: /^[a-zA-Z0-9\s]+$/
};

// Validation rule creators
export const validators = {
  /**
   * Required field validation
   */
  required: (message = 'This field is required') => (value) => {
    if (value === undefined || value === null || value === '') {
      return message;
    }
    if (Array.isArray(value) && value.length === 0) {
      return message;
    }
    return null;
  },

  /**
   * Minimum length validation
   */
  minLength: (min, message) => (value) => {
    if (!value) return null;
    if (value.length < min) {
      return message || `Must be at least ${min} characters`;
    }
    return null;
  },

  /**
   * Maximum length validation
   */
  maxLength: (max, message) => (value) => {
    if (!value) return null;
    if (value.length > max) {
      return message || `Must be no more than ${max} characters`;
    }
    return null;
  },

  /**
   * Pattern matching validation
   */
  pattern: (regex, message = 'Invalid format') => (value) => {
    if (!value) return null;
    if (!regex.test(value)) {
      return message;
    }
    return null;
  },

  /**
   * Email validation
   */
  email: (message = 'Please enter a valid email address') => (value) => {
    if (!value) return null;
    if (!patterns.email.test(value)) {
      return message;
    }
    return null;
  },

  /**
   * Phone number validation
   */
  phone: (message = 'Please enter a valid phone number') => (value) => {
    if (!value) return null;
    if (!patterns.phone.test(value)) {
      return message;
    }
    return null;
  },

  /**
   * URL validation
   */
  url: (message = 'Please enter a valid URL') => (value) => {
    if (!value) return null;
    if (!patterns.url.test(value)) {
      return message;
    }
    return null;
  },

  /**
   * Minimum value validation (for numbers)
   */
  min: (min, message) => (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const numValue = Number(value);
    if (isNaN(numValue)) return 'Must be a number';
    if (numValue < min) {
      return message || `Must be at least ${min}`;
    }
    return null;
  },

  /**
   * Maximum value validation (for numbers)
   */
  max: (max, message) => (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const numValue = Number(value);
    if (isNaN(numValue)) return 'Must be a number';
    if (numValue > max) {
      return message || `Must be no more than ${max}`;
    }
    return null;
  },

  /**
   * Custom validation function
   */
  custom: (validateFn) => validateFn,

  /**
   * Match another field (e.g., password confirmation)
   */
  matches: (fieldName, message = 'Fields do not match') => (value, allValues) => {
    if (!value) return null;
    if (value !== allValues?.[fieldName]) {
      return message;
    }
    return null;
  }
};

/**
 * Validate a single field with multiple rules
 */
export const validateField = (value, rules, allValues = {}) => {
  if (!rules || rules.length === 0) return null;

  for (const rule of rules) {
    const error = rule(value, allValues);
    if (error) return error;
  }
  return null;
};

/**
 * Validate entire form
 */
export const validateForm = (values, schema) => {
  const errors = {};
  let isValid = true;

  for (const [fieldName, rules] of Object.entries(schema)) {
    const error = validateField(values[fieldName], rules, values);
    if (error) {
      errors[fieldName] = error;
      isValid = false;
    }
  }

  return { isValid, errors };
};

/**
 * Create a validation schema builder
 */
export const createSchema = () => {
  const schema = {};

  return {
    field: (name, ...rules) => {
      schema[name] = rules;
      return this;
    },
    build: () => schema
  };
};

/**
 * Hook for form validation
 */
export const useFormValidation = (initialValues = {}, validationSchema = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const initialValuesRef = useRef(initialValues);

  /**
   * Handle field change
   */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setValues((prev) => {
      const newValues = { ...prev, [name]: fieldValue };
      setIsDirty(JSON.stringify(newValues) !== JSON.stringify(initialValuesRef.current));
      return newValues;
    });

    // Clear error on change if field was touched
    if (touched[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [touched]);

  /**
   * Set a field value programmatically
   */
  const setFieldValue = useCallback((name, value) => {
    setValues((prev) => {
      const newValues = { ...prev, [name]: value };
      setIsDirty(JSON.stringify(newValues) !== JSON.stringify(initialValuesRef.current));
      return newValues;
    });
  }, []);

  /**
   * Handle field blur
   */
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate on blur
    if (validationSchema[name]) {
      const error = validateField(values[name], validationSchema[name], values);
      if (error) {
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    }
  }, [values, validationSchema]);

  /**
   * Validate all fields
   */
  const validate = useCallback(() => {
    const { isValid, errors: validationErrors } = validateForm(values, validationSchema);
    setErrors(validationErrors);
    
    // Mark all fields as touched
    const allTouched = Object.keys(validationSchema).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    return isValid;
  }, [values, validationSchema]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback((onSubmit) => async (e) => {
    e?.preventDefault();
    
    const isValid = validate();
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      await onSubmit(values);
      setIsDirty(false);
      initialValuesRef.current = values;
    } finally {
      setIsSubmitting(false);
    }
  }, [validate, values]);

  /**
   * Reset form to initial values
   */
  const resetForm = useCallback((newInitialValues) => {
    const resetValues = newInitialValues || initialValuesRef.current;
    setValues(resetValues);
    setErrors({});
    setTouched({});
    setIsDirty(false);
    if (newInitialValues) {
      initialValuesRef.current = newInitialValues;
    }
  }, []);

  /**
   * Set multiple errors
   */
  const setFormErrors = useCallback((formErrors) => {
    setErrors((prev) => ({ ...prev, ...formErrors }));
  }, []);

  /**
   * Get field props for easy binding
   */
  const getFieldProps = useCallback((name) => ({
    name,
    value: values[name] ?? '',
    onChange: handleChange,
    onBlur: handleBlur
  }), [values, handleChange, handleBlur]);

  /**
   * Get field error state
   */
  const getFieldError = useCallback((name) => ({
    error: touched[name] ? errors[name] : null,
    touched: touched[name] || false,
    isValid: touched[name] && !errors[name]
  }), [touched, errors]);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isDirty,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFormErrors,
    validate,
    resetForm,
    getFieldProps,
    getFieldError
  };
};

const formValidation = {
  validators,
  patterns,
  validateField,
  validateForm,
  createSchema,
  useFormValidation
};

export default formValidation;
